import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import imageCompression from 'browser-image-compression';
import { todayLocal, localDateOffset } from '../lib/dateHelpers';
import { uploadFieldReportPhoto, uploadFieldReportSignature } from '../lib/fieldReportUploads';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import JobSelectionCard from '../components/JobSelectionCard';
import { useFieldFormOptions } from '../hooks/useFieldFormOptions';
import LastSavedIndicator from '../components/LastSavedIndicator';
import { 
  Save, 
  Send, 
  Plus, 
  Clock,
  Trash2, 
  Camera, 
  MapPin, 
  Loader2,
  ArrowLeft,
  FileText,
  Image,
  X,
  AlertCircle,
  AlertTriangle,
  Download,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  computeFieldReportPerms,
  photoReadinessLabel,
} from '../utils/fieldReportWorkflow';

const API = process.env.REACT_APP_BACKEND_URL;
import { resolveFileUrl } from '../lib/fileUtils';
import { SafeImage } from '../components/SafeImage';

// Options
const LOCATE_TYPES = ["Public (811)", "Private", "Design / Pre-Construction", "Verification / Re-Locate"];

// Section C — "Utility Found" and "Utility Not Found" share the same list.
// Hardcoded fallback only kicks in if the /api/field-form-options fetch
// fails; the real source of truth lives in Settings → Field Form Options.
const UTILITY_TYPES = [
  "Water", "Telephone", "Cable TV", "Fiber Optic", "Gas",
  "Light Power", "Power", "Duct Bank", "Force Main", "Sanitary Sewer",
  "Storm", "Irrigation", "Pump station", "None", "Other"
];

// Section C — marking method is a multi-select dropdown with write-in.
const MARKING_METHODS = ["Electromagnetic (EM)", "Pipe Horn (PH)"];

const UTILITY_COLORS = [
  { value: "Pink", color: "#ec4899" },
  { value: "Red", color: "#ef4444" },
  { value: "Yellow", color: "#eab308" },
  { value: "Orange", color: "#f97316" },
  { value: "Blue", color: "#3b82f6" },
  { value: "Green", color: "#22c55e" },
  { value: "Purple", color: "#a855f7" },
  { value: "White", color: "#f8fafc" }
];

const MARKINGS_PLACED = ["Paint", "Flags", "Stakes", "Combination", "None"];
const SITE_CONDITIONS = ["Dry", "Wet", "Muddy", "Frozen", "Obstructed"];

// Signature pad component
function SignaturePad({ label, signatureUrl, onSign, gpsData, name, onNameChange, signedAt, required, disabled, onUploadSignature }) {
  const sigRef = useRef(null);
  const [showPad, setShowPad] = useState(false);
  const [capturing, setCapturing] = useState(false);

  const captureSignature = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error('Please sign before capturing');
      return;
    }

    setCapturing(true);

    // Read the canvas FIRST, before requesting GPS. The one-time native
    // "Allow location?" browser prompt sits directly over this button
    // row and a leaked dismiss-tap can land on "Clear" — reading the
    // signature now (and disabling Clear below) closes that timing gap.
    const dataUrl = sigRef.current.toDataURL('image/png');

    let gps = { lat: null, lng: null, accuracy: null };
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { 
          enableHighAccuracy: true, 
          timeout: 10000 
        });
      });
      gps = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy
      };
    } catch (err) {
      console.warn('GPS not available:', err);
    }

    try {
      const uploadedUrl = onUploadSignature ? await onUploadSignature(dataUrl) : dataUrl;
      if (!uploadedUrl) throw new Error('Signature upload failed');
      onSign(uploadedUrl, gps);
      setShowPad(false);
    } catch (err) {
      console.error('Signature upload error:', err);
      toast.error('Failed to upload signature');
    } finally {
      setCapturing(false);
    }
  };

  const clearSignature = () => {
    // Second safety net: once capture has started, the canvas has
    // already been read — never let a leaked tap wipe it mid-upload.
    if (capturing) return;
    if (sigRef.current) {
      sigRef.current.clear();
    }
  };

  return (
    <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
      <Label className="text-sm font-semibold">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      
      <div className="space-y-2">
        <Input
          placeholder="Name"
          value={name || ''}
          onChange={(e) => onNameChange(e.target.value)}
          disabled={disabled}
        />
        
        {signedAt && (
          <p className="text-xs text-slate-500">
            Signed: {new Date(signedAt).toLocaleString()}
          </p>
        )}
        
        {/* GPS display removed per user request */}
      </div>

      {signatureUrl ? (
        <div className="border rounded-lg p-2 bg-white">
          <img src={signatureUrl} alt="Signature" className="max-h-24 mx-auto" />
          {!disabled && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full mt-2"
              onClick={() => {
                onSign(null, null);
                setShowPad(true);
              }}
            >
              Re-sign
            </Button>
          )}
        </div>
      ) : (
        <>
          {showPad ? (
            <div className="space-y-2">
              <div className="border-2 border-slate-300 rounded-lg bg-white touch-none">
                <SignatureCanvas
                  ref={sigRef}
                  canvasProps={{
                    className: 'w-full h-32',
                    style: { width: '100%', height: '128px' }
                  }}
                  backgroundColor="white"
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={clearSignature} disabled={capturing} className="flex-1">
                  Clear
                </Button>
                <Button type="button" size="sm" onClick={captureSignature} disabled={capturing} className="flex-1 bg-green-600 hover:bg-green-700">
                  {capturing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Capture'}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setShowPad(true)}
              disabled={disabled}
            >
              <FileText className="w-4 h-4 mr-2" />
              Tap to Sign
            </Button>
          )}
        </>
      )}
    </div>
  );
}

// Utility Locate Card
function UtilityLocateCard({ utility, index, onChange, onRemove, canRemove, disabled }) {
  // Pull editable option lists from the Field Form Options settings;
  // hardcoded constants are used as instant fallback while the fetch
  // is in flight or if the backend is unreachable. Any currently-selected
  // values that are no longer in the settings show as "removed" badges.
  const utilityFoundOptions = useFieldFormOptions(
    'locate.utility_type', UTILITY_TYPES, utility.utility_type
  );
  const utilityNotFoundOptions = useFieldFormOptions(
    'locate.utility_not_found', UTILITY_TYPES, utility.utility_not_found
  );
  const markingMethodOptions = useFieldFormOptions(
    'locate.marking_methods', MARKING_METHODS, utility.marking_methods
  );
  const colorOptions = useFieldFormOptions(
    'locate.marked_color', UTILITY_COLORS, utility.marked_color
  );

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-['Barlow_Condensed']">Utility Locate #{index + 1}</CardTitle>
          {canRemove && !disabled && (
            <Button type="button" variant="ghost" size="sm" onClick={onRemove}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-sm">Utility Found <span className="text-red-500">*</span></Label>
          <MultiSelectDropdown
            options={utilityFoundOptions}
            value={utility.utility_type}
            onChange={(arr) => onChange({ ...utility, utility_type: arr })}
            disabled={disabled}
            allowCustom
            testIdBase={`utility-${index}-type`}
            className="mt-1"
          />
        </div>

        <div>
          <Label className="text-sm">Utility Not Found (if any)</Label>
          <MultiSelectDropdown
            options={utilityNotFoundOptions}
            value={utility.utility_not_found}
            onChange={(arr) => onChange({ ...utility, utility_not_found: arr })}
            disabled={disabled}
            allowCustom
            testIdBase={`utility-${index}-not-found`}
            className="mt-1"
          />
        </div>

        <div>
          <Label className="text-sm">Utility Owner (If known)</Label>
          <Input
            value={utility.utility_owner || ''}
            onChange={(e) => onChange({ ...utility, utility_owner: e.target.value })}
            placeholder="Owner name"
            disabled={disabled}
          />
        </div>

        <div>
          <Label className="text-sm">Marking Method Used</Label>
          <MultiSelectDropdown
            options={markingMethodOptions}
            value={utility.marking_methods}
            onChange={(arr) => onChange({ ...utility, marking_methods: arr })}
            disabled={disabled}
            allowCustom
            testIdBase={`utility-${index}-marking-method`}
            className="mt-1"
          />
        </div>

        <div>
          <Label className="text-sm">Marked Color</Label>
          <MultiSelectDropdown
            options={colorOptions.map(c => ({ value: c.value, label: c.value, color: c.color, legacy: c.legacy }))}
            value={utility.marked_color}
            onChange={(arr) => onChange({ ...utility, marked_color: arr })}
            disabled={disabled}
            testIdBase={`utility-${index}-color`}
            className="mt-1"
            renderOption={(opt) => (
              <span className="flex items-center gap-2 text-sm">
                <span
                  className="w-4 h-4 rounded-full border border-slate-300"
                  style={{ backgroundColor: opt.color || '#e5e7eb' }}
                />
                {opt.label}
                {opt.legacy && (
                  <span className="ml-1 text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-1 rounded">
                    removed
                  </span>
                )}
              </span>
            )}
          />
        </div>

        <div>
          <Label className="text-sm">Notes for this utility</Label>
          <Textarea
            value={utility.notes || ''}
            onChange={(e) => onChange({ ...utility, notes: e.target.value })}
            placeholder="Additional notes..."
            rows={2}
            disabled={disabled}
          />
        </div>
      </CardContent>
    </Card>
  );
}

// Photo Card
function LocatePhotoCard({ photo, onRemove, onCaptionChange, disabled }) {
  return (
    <div className="relative border rounded-lg overflow-hidden bg-white">
      <SafeImage
        src={resolveFileUrl(photo)}
        alt="Locate"
        // object-contain so the full photo is always visible — was
        // object-cover, which cropped portrait shots to fit the box.
        className="w-full h-64 object-contain bg-slate-50"
        loading="lazy"
        decoding="async"
      />
      {!disabled && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="absolute top-2 right-2 bg-white/80 hover:bg-white"
          onClick={onRemove}
        >
          <X className="w-4 h-4 text-red-500" />
        </Button>
      )}
      <div className="p-2">
        <Textarea
          placeholder="Add caption..."
          value={photo.caption || ''}
          onChange={(e) => onCaptionChange(e.target.value)}
          className="text-sm resize-y min-h-[44px]"
          rows={2}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

// Main Locate Form Component
export default function LocateForm() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, token, isAdmin } = useAuth();
  const isEditing = !!id;
  const isViewOnlyParam = searchParams.get('view') === 'true';

  const [loading, setLoading] = useState(false);
  // True when loadLocate hit a network/timeout/5xx error (not a real
  // 404/403) — shows an inline Retry instead of booting the tech back to
  // the Field Reports list on a bad-signal blip (2026-09-01, same fix as
  // SoftDigForm.js).
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Counts photos currently mid-upload. Mirrored to backend via
  // expected_photos_count / received_photos_count / photos_ready so
  // reviewers can see whether the form is truly ready for approval.
  const [pendingUploads, setPendingUploads] = useState(0);
  const fileInputRef = useRef(null);
  // Cover photo (Section E) upload — separate input so the file picker can
  // be wired to "Upload Image of Locate Here" without affecting the
  // multi-photo gallery picker below.
  const coverImageInputRef = useRef(null);
  const [coverUploading, setCoverUploading] = useState(false);
  // Track id locally so newly-created drafts have an id for photo uploads
  const [currentId, setCurrentId] = useState(id || null);
  // Job selection state
  const [availableJobs, setAvailableJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  
  // Support both 'job' and 'job_id' query params for flexibility
  const jobIdFromParams = searchParams.get('job') || searchParams.get('job_id') || '';

  const [formData, setFormData] = useState({
    job_id: jobIdFromParams,
    job_number: '',
    assigned_team: '',
    site_location: '',
    locate_date: todayLocal(),
    locate_technician: user?.full_name || '',
    locate_type: [],
    ticket_number: '',
    project_name: '',
    client_requestor: '',
    project_number: '',
    valid_until: localDateOffset(9),
    utilities: [{ id: crypto.randomUUID(), utility_type: [], utility_not_found: [], utility_owner: '', marking_methods: [], marked_color: [], notes: '' }],
    markings_placed: [],
    site_conditions: [],
    locate_image: null,
    locate_photos: [],
    general_notes: '',
    submitted_by: user?.full_name || '',
    submitter_name: user?.full_name || '',
    submitter_signed_at: '',
    submitter_signature_url: '',
    submitter_gps_lat: null,
    submitter_gps_lng: null,
    submitter_gps_accuracy: null,
    reviewer_name: '',
    reviewer_signed_at: '',
    reviewer_signature_url: '',
    reviewer_gps_lat: null,
    reviewer_gps_lng: null,
    reviewer_gps_accuracy: null,
    status: 'draft'
  });

  // Editable option lists from /api/field-form-options.
  // Hardcoded constants are used as instant fallback while the fetch is in
  // flight or if the backend is unreachable. Any previously-saved values
  // that are no longer in the admin list are surfaced with a "removed"
  // badge so existing reports can still be edited without losing data.
  const locateTypeOptions = useFieldFormOptions(
    'locate.locate_type', LOCATE_TYPES, formData.locate_type
  );
  const markingsPlacedOptions = useFieldFormOptions(
    'locate.markings_placed', MARKINGS_PLACED, formData.markings_placed
  );
  const siteConditionsOptions = useFieldFormOptions(
    'locate.site_conditions', SITE_CONDITIONS, formData.site_conditions
  );

  // Determine if form is view-only and submitter-edit rules.
  // Mirrors backend /utils/field_report_workflow.py rules:
  //   - completed -> locked (admin/super_user only)
  //   - pending review -> submitter OR approver can edit
  //                       (submitter signature stays locked)
  //   - draft -> submitter/approver edits
  const canApproveFieldReports = () => {
    if (isAdmin()) return true;
    const permissions = user?.permissions || {};
    return permissions.can_approve_field_reports === true;
  };

  const {
    isCompleted,
    isPending,
    isSubmitter,
    isAdminOrSuper,
    isViewOnly,
    submitterSignatureLocked,
    canAdminUnlock,
    isAdminEditingNonDraft,
  } = computeFieldReportPerms({
    formData,
    user,
    viewParamSet: isViewOnlyParam,
    canApprove: canApproveFieldReports(),
  });

  // Unlock the form so admin/approver can fix a mistake. Idempotent.
  const handleAdminEdit = () => {
    if (!isViewOnly) return;
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    setSearchParams(next, { replace: true });
  };

  // Save admin edits without touching status — reuses the same PUT path
  // submitters use for pending-fix edits.
  const handleAdminSaveEdits = async () => {
    await handleSaveSubmitterEdits();
  };

  // Reviewer-edit affordance preserved for back-compat with existing JSX.
  const canAdminEdit = canApproveFieldReports() && isPending;
  const isSubmittedOrCompleted = isPending || isCompleted;
  // Banner copy + a hard-block on the Approve button while uploads are pending.
  const photoBannerText = photoReadinessLabel({
    expected: formData.expected_photos_count,
    received: formData.received_photos_count,
    photos_ready: formData.photos_ready,
  });
  const photosNotReady = pendingUploads > 0 || photoBannerText !== null;

  // Autosave timer
  const autosaveRef = useRef(null);
  // Critical safety flag: do NOT allow autosave or any save-draft to PUT
  // until the form has finished loading from the server. Otherwise the
  // 10-second autosave timer fires with the empty default formData and
  // wipes a real (often submitted) record on the server. This is what
  // caused the "form opened on desktop, came back, all data gone, status
  // back to draft" bug observed on May 20, 2026 (record 8c5d7ae3).
  const formLoadedRef = useRef(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Load available jobs on mount
  useEffect(() => {
    loadAvailableJobs();
  }, [token]);

  // Always start the form scrolled to the top (fixes "opens at bottom"
  // bug when navigating in from the bottom of a Job detail page).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-select job if job_id comes from URL params (when creating new report from Job page).
  // Fall back to a single targeted fetch if the job isn't in the active-job dropdown
  // (e.g., status = "Ready to schedule" or any non-active status the form would otherwise skip).
  useEffect(() => {
    if (isEditing || !jobIdFromParams || selectedJob || loadingJobs) return;
    const inList = availableJobs.find(j => j.id === jobIdFromParams);
    if (inList) {
      handleJobSelect(jobIdFromParams);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/jobs/${jobIdFromParams}/field-report-prefill`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (cancelled || !res.data?.id) return;
        const job = res.data;
        setAvailableJobs(prev => prev.some(j => j.id === job.id) ? prev : [...prev, job]);
        setSelectedJob(job);
        setFormData(prev => ({
          ...prev,
          job_id: job.id,
          job_number: job.job_number,
          site_location: job.service_address || '',
          assigned_team: job.assigned_team_names?.join(', ') || '',
          locate_date: job.date || prev.locate_date,
          locate_technician: user?.full_name || prev.locate_technician,
          project_name: job.job_title || '',
          client_requestor: job.client_name || '',
        }));
      } catch (err) {
        console.warn('Prefill fetch failed for job', jobIdFromParams, err);
      }
    })();
    return () => { cancelled = true; };
  }, [availableJobs, jobIdFromParams, isEditing, selectedJob, loadingJobs]);

  useEffect(() => {
    if (isEditing && availableJobs.length > 0) {
      loadLocate();
    }
  }, [id, availableJobs]);

  useEffect(() => {
    if (!isEditing || formData.status !== 'draft' || isViewOnly) return;
    // SAFETY: never start the autosave loop until loadLocate has populated
    // formData with the server's real values. Otherwise the very first
    // tick fires with the empty default state and wipes the record.
    if (!formLoadedRef.current) return;

    autosaveRef.current = setInterval(() => {
      handleSaveDraft(true);
    }, 10000);

    return () => {
      if (autosaveRef.current) {
        clearInterval(autosaveRef.current);
      }
    };
  }, [isEditing, formData.status, isViewOnly]);

  const loadAvailableJobs = async () => {
    setLoadingJobs(true);
    try {
      const response = await axios.get(`${API}/api/jobs/for-field-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableJobs(response.data || []);
    } catch (error) {
      console.error('Failed to load jobs:', error);
      toast.error('Failed to load available jobs');
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleJobSelect = (jobId) => {
    const job = availableJobs.find(j => j.id === jobId);
    if (job) {
      setSelectedJob(job);
      // Auto-populate fields from job
      setFormData(prev => ({
        ...prev,
        job_id: job.id,
        job_number: job.job_number,
        site_location: job.service_address || '',
        assigned_team: job.assigned_team_names?.join(', ') || '',
        locate_date: job.date || todayLocal(),
        locate_technician: user?.full_name || '',
        project_name: job.job_title || '',
        client_requestor: job.client_name || ''
      }));
    }
  };

  // Form is disabled until a job is selected (for new forms)
  const isFormDisabled = !isEditing && !formData.job_id;

  const loadLocate = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await axios.get(`${API}/api/locates/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const locateData = response.data;
      
      // Find and set the selected job if editing
      if (locateData.job_id) {
        const job = availableJobs.find(j => j.id === locateData.job_id);
        if (job) {
          setSelectedJob(job);
          // Update assigned_team from current job data (not stale stored value)
          locateData.assigned_team = job.assigned_team_names?.join(', ') || locateData.assigned_team || '';
        }
      }
      
      setFormData(locateData);
      formLoadedRef.current = true;
    } catch (error) {
      // Only leave the form when the server has definitively said "no" —
      // record truly gone (404) or no permission (403). A network hiccup
      // (timeout, offline, 5xx) must NOT boot the tech back to the list.
      // Show an inline retry instead.
      const status = error.response?.status;
      if (status === 404 || status === 403) {
        toast.error(status === 404 ? 'Locate report not found' : "You don't have permission to view this report");
        navigate('/field-reports');
      } else {
        setLoadError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveDraft = async (isAutosave = false) => {
    if (isViewOnly) return;
    // SAFETY: never PUT empty formData over a real record on the server.
    // If this is an existing record (`isEditing`) and the load hasn't
    // finished yet, refuse to save. The autosave loop will pick up again
    // once `formLoadedRef.current` flips true via loadLocate().
    if (isEditing && !formLoadedRef.current) return;
    // SAFETY: never let "save draft" un-submit an already-submitted
    // record. handleSaveDraft hard-codes status='draft', so if a user
    // accidentally clicks Save Draft on a submitted/approved form (or
    // a stale autosave timer fires after submission), we would revert
    // the status. Only allow this path on actual drafts.
    if (isEditing && formData.status && formData.status !== 'draft') return;

    setSaving(true);
    try {
      const payload = { ...formData, status: 'draft' };
      
      let response;
      if (currentId) {
        response = await axios.put(`${API}/api/locates/${currentId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(`${API}/api/locates`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const newId = response.data?.id;
        if (newId) {
          setCurrentId(newId);
          navigate(`/locate/${newId}`, { replace: true });
        }
      }
      
      setLastSaved(new Date());
      if (!isAutosave) {
        toast.success('Draft saved');
      }
    } catch (error) {
      if (!isAutosave) {
        toast.error('Failed to save draft');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    // Confirm before submit — multiple field techs have accidentally
    // tapped Submit thinking it was Save Draft, then couldn't find or
    // edit the locked record. One quick confirm dialog eliminates that
    // class of mistake without adding a new modal dependency.
    if (!window.confirm("Submit this locate for review?\n\nOnce submitted, your signature is locked and the report moves to Pending Review. You can still edit it until the reviewer approves, but you can't undo the submission.")) {
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...formData };
      
      if (currentId) {
        await axios.put(`${API}/api/locates/${currentId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        const createResponse = await axios.post(`${API}/api/locates`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const newId = createResponse.data?.id;
        if (newId) setCurrentId(newId);
        await axios.post(`${API}/api/locates/${newId}/submit`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Locate report submitted!');
        navigate(`/locate/${newId}?view=true`);
        return;
      }
      
      await axios.post(`${API}/api/locates/${currentId}/submit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Locate report submitted!');
      navigate(`/locate/${currentId}?view=true`);
    } catch (error) {
      // Scroll to top + keep the toast up longer — Submit sits at the
      // bottom of a long form, so a top-right error toast can be missed
      // entirely if the tech doesn't scroll back up (2026-09-09 field
      // report: tech couldn't tell why submit "did nothing").
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (error.response?.data?.detail?.errors) {
        const errors = error.response.data.detail.errors;
        toast.error(
          <div>
            <p className="font-bold mb-1">Missing required fields:</p>
            <ul className="text-sm list-disc pl-4">
              {errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
              {errors.length > 5 && <li>...and {errors.length - 5} more</li>}
            </ul>
          </div>,
          { duration: 8000 }
        );
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail, { duration: 8000 });
      } else {
        toast.error('Failed to submit report', { duration: 8000 });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Submitter editing a report that is already in Pending Review.
  // PUT without changing status — backend enforces signature lock.
  const handleSaveSubmitterEdits = async () => {
    if (!currentId) return;
    setSaving(true);
    try {
      const payload = { ...formData };
      delete payload.status; // never let this path alter the workflow status
      await axios.put(`${API}/api/locates/${currentId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLastSaved(new Date());
      toast.success('Changes saved — still pending review.');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async () => {
    if (!formData.reviewer_signature_url) {
      toast.error('Please sign the Manager Approval signature pad before approving.');
      return;
    }
    setApproving(true);
    try {
      await axios.put(`${API}/api/locates/${id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await axios.post(`${API}/api/locates/${id}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Report approved!');
      loadLocate();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to approve report';
      toast.error(typeof msg === 'string' ? msg : 'Failed to approve report');
    } finally {
      setApproving(false);
    }
  };

  const addUtility = () => {
    setFormData(prev => ({
      ...prev,
      utilities: [
        ...prev.utilities,
        { id: crypto.randomUUID(), utility_type: [], utility_not_found: [], utility_owner: '', marking_methods: [], marked_color: [], notes: '' }
      ]
    }));
  };

  const updateUtility = (index, data) => {
    setFormData(prev => ({
      ...prev,
      utilities: prev.utilities.map((u, i) => i === index ? data : u)
    }));
  };

  const removeUtility = (index) => {
    setFormData(prev => ({
      ...prev,
      utilities: prev.utilities.filter((_, i) => i !== index)
    }));
  };

  // Ensure we have a saved draft id before uploading any photos/signatures.
  // RACE LOCK to prevent duplicate drafts from rapid concurrent uploads.
  const draftCreationRef = useRef(null);
  const ensureDraftId = async () => {
    if (currentId) return currentId;
    if (draftCreationRef.current) return draftCreationRef.current;
    draftCreationRef.current = (async () => {
      try {
        // Belt-and-suspenders: if formData hasn't been hydrated from the
        // selected job yet (race between fast tap-to-upload and the
        // handleJobSelect setFormData), pull project_name / job_number /
        // etc. directly off selectedJob so the new draft is never an
        // "(Untitled draft)" in the list view.
        const fromJob = selectedJob ? {
          job_id: selectedJob.id,
          job_number: selectedJob.job_number || '',
          project_name: selectedJob.job_title || '',
          site_location: selectedJob.service_address || '',
          client_requestor: selectedJob.client_name || '',
          assigned_team: selectedJob.assigned_team_names?.join(', ') || '',
        } : {};
        const payload = { ...formData, ...fromJob, status: 'draft' };
        const res = await axios.post(`${API}/api/locates`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const newId = res.data?.id;
        if (newId) {
          setCurrentId(newId);
          navigate(`/locate/${newId}`, { replace: true });
          return newId;
        }
      } catch (err) {
        console.error('Failed to create draft for upload:', err);
        toast.error('Could not save draft before upload');
        draftCreationRef.current = null;
      }
      return null;
    })();
    return await draftCreationRef.current;
  };

  const uploadPhoto = async (file) => {
    const formId = await ensureDraftId();
    if (!formId) return null;
    try {
      const res = await uploadFieldReportPhoto('locates', formId, file, token);
      return res?.url || null;
    } catch (err) {
      console.error('Photo upload failed:', err);
      toast.error('Photo upload failed');
      return null;
    }
  };

  const uploadSignature = async (dataUrl) => {
    const formId = await ensureDraftId();
    if (!formId) return null;
    try {
      const res = await uploadFieldReportSignature('locates', formId, dataUrl, token);
      return res?.url || null;
    } catch (err) {
      console.error('Signature upload failed:', err);
      toast.error('Signature upload failed');
      return null;
    }
  };

  // Persist the photo-upload progress fields so reviewers see "photos
  // still uploading" warnings in real time. Tolerant of failures — never
  // blocks the upload flow.
  const persistPhotoTracking = async (extra = {}) => {
    if (!currentId) return;
    try {
      const photos = (formData.locate_photos || []).length + (extra._receivedDelta || 0);
      const expected = formData.expected_photos_count != null
        ? Math.max(formData.expected_photos_count, photos)
        : photos;
      const payload = {
        received_photos_count: photos,
        photos_ready: photos >= expected,
        ...extra,
      };
      delete payload._receivedDelta;
      await axios.put(`${API}/api/locates/${currentId}`, payload);
    } catch (e) {
      // Submitter can be view-only or backend may 403 in some edge cases —
      // a tracking PUT failure is non-fatal.
      console.warn('photo tracking PUT failed', e?.response?.status);
    }
  };

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Tell the backend up-front how many photos we expect this batch to add.
    const expectedAfter = (formData.locate_photos || []).length + files.length;
    setFormData(prev => ({
      ...prev,
      expected_photos_count: Math.max(prev.expected_photos_count || 0, expectedAfter),
      photos_ready: false,
    }));
    setPendingUploads(p => p + files.length);
    if (currentId) {
      try {
        await axios.put(`${API}/api/locates/${currentId}`, {
          expected_photos_count: expectedAfter,
          photos_ready: false,
        });
      } catch (err) {
        console.warn('expected_photos_count PUT failed', err?.response?.status);
      }
    }

    setUploading(true);

    // Running accumulator — preserves the complete photo list across loop
    // iterations so each PUT sends the FULL current array. Without this,
    // React's closure pins formData.locate_photos to its value at function
    // entry, so every PUT in a multi-photo batch overwrites the prior one
    // (root cause of the blank-tile bug from commit 707ba365).
    let runningPhotos = formData.locate_photos || [];

    for (const file of files) {
      try {
        const options = {
          // Compressed for fast load and small PDFs. 1280 px max + ~0.5 MB
          // still renders crisp for utility-locate documentation; the form
          // and PDF both scale photos into a fixed visual box.
          maxSizeMB: 0.3,
          maxWidthOrHeight: 1280,
          initialQuality: 0.82,
          useWebWorker: true,
          fileType: 'image/jpeg'
        };
        const compressedBlob = await imageCompression(file, options);
        const compressedFile = new File([compressedBlob], file.name, { type: compressedBlob.type || 'image/jpeg', lastModified: Date.now() });

        // Upload to OneDrive immediately; store only the URL in state
        const url = await uploadPhoto(compressedFile);
        if (!url) {
          // Silent backend failure: keep expected_photos_count in sync so
          // the reviewer doesn't see a permanent "5 of 6 photos uploaded"
          // banner. Surface to the submitter so they can retry.
          setFormData(prev => ({
            ...prev,
            expected_photos_count: Math.max(0, (prev.expected_photos_count || 1) - 1),
          }));
          setPendingUploads(p => Math.max(0, p - 1));
          toast.error(`Photo upload failed: ${file.name}. Please try again.`);
          continue;
        }
        const newPhoto = {
          id: crypto.randomUUID(),
          url,
          caption: ''
        };
        runningPhotos = [...runningPhotos, newPhoto];
        setFormData(prev => {
          const exp = Math.max(prev.expected_photos_count || 0, runningPhotos.length);
          return {
            ...prev,
            locate_photos: runningPhotos,
            received_photos_count: runningPhotos.length,
            photos_ready: runningPhotos.length >= exp,
          };
        });
        setPendingUploads(p => Math.max(0, p - 1));
        // Persist progress so reviewers see it tick up live (only useful
        // when the form has been submitted and the submitter is still
        // adding photos — backend now allows submitter PUTs while pending).
        if (currentId && formData.status !== 'draft') {
          try {
            await axios.put(`${API}/api/locates/${currentId}`, {
              locate_photos:         runningPhotos,
              received_photos_count: runningPhotos.length,
            });
          } catch (_) {}
        }
      } catch (error) {
        console.error('Photo upload error:', error);
        toast.error(`Failed to upload ${file.name}`);
        setPendingUploads(p => Math.max(0, p - 1));
        // Keep expected count honest so we don't leave the reviewer stuck.
        setFormData(prev => ({
          ...prev,
          expected_photos_count: Math.max(0, (prev.expected_photos_count || 1) - 1),
        }));
      }
    }

    setUploading(false);
    e.target.value = '';
    // Final reconciliation — set photos_ready true once everything settled.
    await persistPhotoTracking({ photos_ready: true });
  };

  const removePhoto = (photoId) => {
    // Optimistic local update — UI stays snappy.
    setFormData(prev => {
      const next = prev.locate_photos.filter(p => p.id !== photoId);
      // When a user deletes an already-uploaded photo, the expected count
      // must drop with it. Otherwise the reviewer sees "N-1 of N photos
      // uploaded" forever and can't approve.
      const nextExpected = Math.max(0, (prev.expected_photos_count || next.length) - 1);
      return {
        ...prev,
        locate_photos: next,
        expected_photos_count: nextExpected,
        received_photos_count: next.length,
        photos_ready: next.length >= nextExpected,
      };
    });
    // Persist via DELETE so the backend ALSO removes the underlying
    // OneDrive file (previous code used PUT which just rewrote the array
    // and left the file orphaned). The DELETE endpoint atomically updates
    // the array + recomputes count fields + cleans storage.
    if (currentId) {
      axios.delete(`${API}/api/locates/${currentId}/photos/${photoId}`)
        .catch(() => { /* non-fatal; UI is already updated */ });
    }
  };

  const updatePhotoCaption = (photoId, caption) => {
    setFormData(prev => ({
      ...prev,
      locate_photos: prev.locate_photos.map(p => 
        p.id === photoId ? { ...p, caption } : p
      )
    }));
  };

  // Section E cover photo: required, ~300 KB compressed JPG. Stored on the
  // record as `locate_image = { id, url, caption }` (singular, not array).
  // Re-selecting replaces the previous cover; backend keeps the old
  // OneDrive file (no cascading delete) because field reports often want
  // to recover a prior image, and the orphan is cleaned up when the form
  // is deleted.
  const handleCoverImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const compressedBlob = await imageCompression(file, {
        maxSizeMB: 0.3,           // 300 KB cap per spec
        maxWidthOrHeight: 1600,
        initialQuality: 0.82,
        useWebWorker: true,
        fileType: 'image/jpeg',
      });
      const compressedFile = new File(
        [compressedBlob],
        file.name,
        { type: compressedBlob.type || 'image/jpeg', lastModified: Date.now() },
      );
      const url = await uploadPhoto(compressedFile);
      if (!url) return;
      const cover = { id: crypto.randomUUID(), url, caption: '' };
      setFormData(prev => ({ ...prev, locate_image: cover }));
      // Persist immediately when editing an existing record so the
      // reviewer sees the cover photo without waiting for the next save.
      if (currentId) {
        try {
          await axios.put(`${API}/api/locates/${currentId}`, { locate_image: cover }, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch (_) {/* non-fatal */}
      }
      toast.success('Cover image uploaded');
    } catch (err) {
      console.error('Cover image upload failed', err);
      toast.error('Failed to upload cover image');
    } finally {
      setCoverUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  const removeCoverImage = () => {
    setFormData(prev => ({ ...prev, locate_image: null }));
    if (currentId) {
      axios.put(`${API}/api/locates/${currentId}`, { locate_image: null }, {
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {/* non-fatal */});
    }
  };

  const handleSubmitterSign = (dataUrl, gps) => {
    setFormData(prev => ({
      ...prev,
      submitter_signature_url: dataUrl,
      submitter_gps_lat: gps?.lat,
      submitter_gps_lng: gps?.lng,
      submitter_gps_accuracy: gps?.accuracy,
      submitter_signed_at: dataUrl ? new Date().toISOString() : ''
    }));
  };

  const handleReviewerSign = (dataUrl, gps) => {
    setFormData(prev => ({
      ...prev,
      reviewer_signature_url: dataUrl,
      reviewer_gps_lat: gps?.lat,
      reviewer_gps_lng: gps?.lng,
      reviewer_gps_accuracy: gps?.accuracy,
      reviewer_signed_at: dataUrl ? new Date().toISOString() : ''
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 px-6 text-center" data-testid="locate-load-error">
        <AlertTriangle className="w-10 h-10 text-amber-500" />
        <div>
          <p className="font-medium text-slate-900">Couldn't load this report</p>
          <p className="text-sm text-slate-500 mt-1">Check your connection and try again — your form is still there, nothing was lost.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/field-reports')} data-testid="load-error-back-btn">Back to Field Reports</Button>
          <Button onClick={loadLocate} data-testid="load-error-retry-btn">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pb-[calc(11rem+env(safe-area-inset-bottom))] md:pb-32">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white shadow-sm">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/field-reports')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Utility Locate Form</h1>
              <p className="text-xs text-slate-500">
                {formData.job_number ? `Job: ${formData.job_number}` : 'Center Line Locating'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canAdminUnlock && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100"
                onClick={handleAdminEdit}
                data-testid="admin-edit-report-btn"
                title="Unlock this report to fix a mistake"
              >
                Edit Report
              </Button>
            )}
            {isAdminEditingNonDraft && (
              <Button
                size="sm"
                className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                onClick={handleAdminSaveEdits}
                disabled={saving}
                data-testid="admin-save-edits-btn"
                title="Save changes — does NOT change the report status"
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
            )}
            {formData.status === 'draft' && (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700">Draft</Badge>
            )}
            {formData.status === 'submitted_pending_review' && (
              <Badge className="bg-blue-100 text-blue-700">Pending Review</Badge>
            )}
            {formData.status === 'completed' && (
              <Badge className="bg-green-100 text-green-700">Completed</Badge>
            )}
          </div>
        </div>
        {lastSaved && (
          <div className="px-4 pb-2 text-xs text-slate-400">
            Last saved: {lastSaved.toLocaleTimeString()}
          </div>
        )}
      </div>

      {/* Status Banner for submitted/completed forms */}
      {(formData.status === 'submitted_pending_review' || formData.status === 'completed') && (
        <div className={`mx-4 mt-4 p-4 rounded-lg ${formData.status === 'completed' ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'}`}>
          <div className="flex items-center gap-3">
            {formData.status === 'completed' ? (
              <MapPin className="w-6 h-6 text-green-600" />
            ) : (
              <AlertCircle className="w-6 h-6 text-blue-600" />
            )}
            <div>
              <p className={`font-semibold ${formData.status === 'completed' ? 'text-green-800' : 'text-blue-800'}`}>
                {formData.status === 'completed' ? 'Report Completed' : 'Submitted - Pending Review'}
              </p>
              <p className={`text-sm ${formData.status === 'completed' ? 'text-green-600' : 'text-blue-600'}`}>
                {formData.status === 'completed' 
                  ? `Reviewed by ${formData.reviewed_by || 'Manager'} on ${formData.reviewed_at ? new Date(formData.reviewed_at).toLocaleDateString() : 'N/A'}`
                  : `Submitted by ${formData.submitted_by || formData.locate_technician} on ${formData.submitted_at ? new Date(formData.submitted_at).toLocaleDateString() : 'N/A'}`
                }
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="p-4 space-y-6 max-w-2xl mx-auto">
        {/* Job Selection — Feb 2026 audit: swapped the byte-identical
            hand-rolled block for the shared <JobSelectionCard>. Zero
            visible UX change; keeps this form in lockstep with PreCon
            and CSE for future updates. */}
        <JobSelectionCard
          value={formData.job_id}
          onChange={handleJobSelect}
          availableJobs={availableJobs}
          loadingJobs={loadingJobs}
          selectedJob={selectedJob}
          disabled={isViewOnly || isEditing}
          testIdPrefix="locate-job-selection"
        />

        {/* Warning if no job selected */}
        {isFormDisabled && (
          <div className="bg-amber-100 border border-amber-300 rounded-lg p-4 text-center">
            <AlertCircle className="w-6 h-6 mx-auto text-amber-600 mb-2" />
            <p className="text-amber-800 font-medium">Please select a job to continue</p>
            <p className="text-sm text-amber-600">The form will be enabled once a job is selected</p>
          </div>
        )}

        {/* Section A: Locate Information */}
        <Card className={isFormDisabled ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">A</span>
              Locate Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Assigned Team <span className="text-slate-400 text-xs">(from Job - read only)</span></Label>
              <Input
                value={formData.assigned_team}
                placeholder="Auto-filled from job"
                disabled
                className="bg-slate-100"
              />
            </div>
            <div>
              <Label>Site Location / Address <span className="text-slate-400 text-xs">(from Job - read only)</span></Label>
              <Input
                value={formData.site_location}
                placeholder="Auto-filled from job"
                disabled
                className="bg-slate-100"
              />
            </div>
            <div>
              <Label>Date of Locate <span className="text-red-500">*</span> <span className="text-slate-400 text-xs">(editable)</span></Label>
              <Input
                type="date"
                value={formData.locate_date}
                onChange={(e) => updateField('locate_date', e.target.value)}
                disabled={isViewOnly}
              />
            </div>
            <div>
              <Label>Locate Technician / CLL Lead <span className="text-slate-400 text-xs">(from logged-in user)</span></Label>
              <Input
                value={formData.locate_technician}
                placeholder="Auto-filled from logged-in user"
                disabled
                className="bg-slate-100"
              />
            </div>
            <div>
              <Label>Locate Type</Label>
              <MultiSelectDropdown
                options={locateTypeOptions}
                value={formData.locate_type}
                onChange={(arr) => updateField('locate_type', arr)}
                disabled={isViewOnly}
                testIdBase="locate-type"
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Section B: Ticket & Project Information */}
        <Card className={isFormDisabled ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">B</span>
              Ticket & Project Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>One Call / Ticket Number</Label>
              <Input
                value={formData.ticket_number}
                onChange={(e) => updateField('ticket_number', e.target.value)}
                placeholder="Ticket #"
                disabled={isViewOnly}
              />
            </div>
            <div>
              <Label>Project Name <span className="text-slate-400 text-xs">(from Job)</span></Label>
              <Input
                value={formData.project_name}
                placeholder="Auto-filled from job"
                disabled
                className="bg-slate-100"
              />
            </div>
            <div>
              <Label>Client / Requestor <span className="text-slate-400 text-xs">(from Job)</span></Label>
              <Input
                value={formData.client_requestor}
                placeholder="Auto-filled from job"
                disabled
                className="bg-slate-100"
              />
            </div>
            <div>
              <Label>Project Number / Job ID</Label>
              <Input
                value={formData.project_number}
                onChange={(e) => updateField('project_number', e.target.value)}
                placeholder="Reference number"
                disabled={isViewOnly}
              />
            </div>
            <div>
              <Label>Locate Valid Until <span className="text-slate-400 text-xs">(auto-set: report creation date + 9 days)</span></Label>
              <Input
                type="date"
                value={formData.valid_until}
                disabled
                className="bg-slate-100"
                data-testid="locate-valid-until"
              />
              <p className="text-xs text-slate-500 mt-1">
                Valid for 10 calendar days from the date the report was created. Editing the form does not change this date.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Section C: Utility Locate Details */}
        <Card className={isFormDisabled ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">C</span>
              Utility Locate Details <span className="text-red-500 text-sm">*</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.utilities.map((utility, index) => (
              <UtilityLocateCard
                key={utility.id}
                utility={utility}
                index={index}
                onChange={(data) => updateUtility(index, data)}
                onRemove={() => removeUtility(index)}
                canRemove={formData.utilities.length > 1}
                disabled={isViewOnly}
              />
            ))}

            {!isViewOnly && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={addUtility}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Utility Locate
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Section D: Site Marking & Conditions */}
        <Card className={isFormDisabled ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">D</span>
              Site Marking & Conditions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Markings Placed On Surface</Label>
              <MultiSelectDropdown
                options={markingsPlacedOptions}
                value={formData.markings_placed}
                onChange={(arr) => updateField('markings_placed', arr)}
                disabled={isViewOnly}
                testIdBase="markings-placed"
                className="mt-1"
              />
            </div>
            <div>
              <Label>General Site Conditions</Label>
              <MultiSelectDropdown
                options={siteConditionsOptions}
                value={formData.site_conditions}
                onChange={(arr) => updateField('site_conditions', arr)}
                disabled={isViewOnly}
                testIdBase="site-conditions"
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Section E: Locate Photos */}
        <Card className={isFormDisabled ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">E</span>
              Locate Photos <span className="text-red-500 text-sm">*</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Required cover image — single 300 KB photo. Rendered at the
                top of the Photos tile per the 2026-05-25 spec.
                For Locate forms created BEFORE the redesign cutoff
                (2026-05-26 13:59 UTC, commit 32838749) that never collected
                a cover photo, the upload box is hidden and a clear notice
                is shown instead — those forms predate the requirement. */}
            <div>
              {(() => {
                // Cutoff = commit 32838749 (Iteration-80 Locate redesign).
                // JS Date months are 0-indexed; 4 = May.
                const REDESIGN_CUTOFF_MS = Date.UTC(2026, 4, 26, 13, 59, 0);
                const createdMs = formData.created_at
                  ? Date.parse(formData.created_at)
                  : NaN;
                const isPreRedesign =
                  !Number.isNaN(createdMs) && createdMs < REDESIGN_CUTOFF_MS;
                const hasNoCover = !formData.locate_image?.url;

                if (isPreRedesign && hasNoCover) {
                  return (
                    <div
                      className="rounded-lg border border-slate-300 bg-slate-50 p-4 text-sm"
                      data-testid="locate-cover-image-pre-redesign-notice"
                    >
                      <p className="font-semibold text-slate-700 mb-1">
                        Cover photo not required
                      </p>
                      <p className="text-slate-600">
                        This form was submitted before the May 26, 2026 redesign.
                      </p>
                    </div>
                  );
                }

                return (
                  <>
                    <Label className="text-sm font-semibold">
                      Upload Image of Locate Site Address Here: <span className="text-red-500">*</span>
                    </Label>
                    <input
                      ref={coverImageInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleCoverImageSelect}
                      disabled={isViewOnly}
                      data-testid="locate-cover-image-input"
                    />
                    {formData.locate_image?.url ? (
                      <div className="relative mt-2 border rounded-lg overflow-hidden bg-white">
                        <SafeImage
                          src={resolveFileUrl(formData.locate_image)}
                          alt="Cover"
                          // object-contain so portrait + landscape photos
                          // always show their full content. The bg-slate-50
                          // letterbox keeps the card height predictable.
                          className="w-full h-72 object-contain bg-slate-50"
                          loading="lazy"
                          decoding="async"
                          data-testid="locate-cover-image"
                        />
                        {!isViewOnly && (
                          <div className="absolute top-2 right-2 flex gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="bg-white/80 hover:bg-white"
                              onClick={() => coverImageInputRef.current?.click()}
                              disabled={coverUploading}
                              data-testid="locate-cover-image-replace"
                            >
                              {coverUploading
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Camera className="w-4 h-4" />}
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="bg-white/80 hover:bg-white"
                              onClick={removeCoverImage}
                              disabled={coverUploading}
                              data-testid="locate-cover-image-remove"
                            >
                              <X className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div
                        className="mt-2 border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition-colors h-72 flex flex-col items-center justify-center"
                        onClick={() => !isViewOnly && !coverUploading && coverImageInputRef.current?.click()}
                        data-testid="locate-cover-image-dropzone"
                      >
                        {coverUploading ? (
                          <Loader2 className="w-10 h-10 mx-auto text-amber-500 animate-spin" />
                        ) : (
                          <Image className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                        )}
                        <p className="text-slate-600 text-sm font-medium">
                          {coverUploading ? 'Uploading…' : 'Tap to upload cover image'}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          One photo, automatically compressed to ~300 KB
                        </p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="border-t border-slate-100 pt-4">
              <Label className="text-sm font-semibold">Additional Locate Photos</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handlePhotoSelect}
                disabled={isViewOnly}
              />

              {formData.locate_photos.length > 0 ? (
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {formData.locate_photos.map(photo => (
                    <LocatePhotoCard
                      key={photo.id}
                      photo={photo}
                      onRemove={() => removePhoto(photo.id)}
                      onCaptionChange={(caption) => updatePhotoCaption(photo.id, caption)}
                      disabled={isViewOnly}
                    />
                  ))}
                </div>
              ) : (
                <div
                  className="mt-2 border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-amber-500 hover:bg-amber-50 transition-colors"
                  onClick={() => !isViewOnly && fileInputRef.current?.click()}
                >
                  <Image className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-600">Tap to add photos</p>
                  <p className="text-xs text-slate-400 mt-1">At least 1 photo required</p>
                </div>
              )}

              {!isViewOnly && formData.locate_photos.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mt-3"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Add More Photos
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Section F: General Notes */}
        <Card className={isFormDisabled ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">F</span>
              General Locate Notes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={formData.general_notes}
              onChange={(e) => updateField('general_notes', e.target.value)}
              placeholder="Additional notes..."
              rows={4}
              disabled={isViewOnly}
            />
          </CardContent>
        </Card>

        {/* Section G: Signatures */}
        <Card className={isFormDisabled ? 'opacity-50 pointer-events-none' : ''}>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">G</span>
              Signatures
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Instructions for form submission */}
            {formData.status === 'draft' && !canApproveFieldReports() && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-700">
                <p className="font-medium mb-1">Instructions:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Sign your signature below</li>
                  <li>Click the green "Submit for Approval" button</li>
                  <li>Your manager will review and approve the form</li>
                </ol>
              </div>
            )}
            
            {/* Show rejection reason if form was rejected */}
            {formData.rejection_reason && formData.status === 'draft' && (
              <div className="p-3 bg-red-50 rounded-lg border border-red-200 text-sm text-red-700">
                <p className="font-medium">Form was rejected:</p>
                <p>{formData.rejection_reason}</p>
                <p className="text-xs mt-1">Rejected by {formData.rejected_by} on {formData.rejected_at ? new Date(formData.rejected_at).toLocaleDateString() : 'N/A'}</p>
              </div>
            )}

            <div>
              <Label>Submitted By</Label>
              <Input
                value={formData.submitted_by}
                onChange={(e) => updateField('submitted_by', e.target.value)}
                disabled
              />
            </div>

            {/* Submitter Signature - Always visible */}
            <SignaturePad
              label="Locate Technician Signature"
              signatureUrl={formData.submitter_signature_url}
              onSign={handleSubmitterSign}
              gpsData={{
                lat: formData.submitter_gps_lat,
                lng: formData.submitter_gps_lng,
                accuracy: formData.submitter_gps_accuracy
              }}
              name={formData.submitter_name}
              onNameChange={(v) => updateField('submitter_name', v)}
              signedAt={formData.submitter_signed_at}
              required
              disabled={isViewOnly || formData.status !== 'draft'}
              onUploadSignature={uploadSignature}
            />

            {/* Reviewer Signature - Only visible to users with approval permission when form is submitted */}
            {(canApproveFieldReports() && formData.status === 'submitted_pending_review') && (
              <SignaturePad
                label="Manager Approval Signature"
                signatureUrl={formData.reviewer_signature_url}
                onSign={handleReviewerSign}
                gpsData={{
                  lat: formData.reviewer_gps_lat,
                  lng: formData.reviewer_gps_lng,
                  accuracy: formData.reviewer_gps_accuracy
                }}
                name={formData.reviewer_name}
                onNameChange={(v) => updateField('reviewer_name', v)}
                signedAt={formData.reviewer_signed_at}
                required={false}
                disabled={false}
                onUploadSignature={uploadSignature}
              />
            )}
            
            {/* Show reviewer signature (read-only) for completed forms */}
            {formData.status === 'completed' && formData.reviewer_signature_url && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <Label className="text-green-700">Manager Approval Signature</Label>
                <img src={formData.reviewer_signature_url} alt="Manager Signature" className="max-h-24 mt-2" />
                <p className="text-xs text-green-600 mt-1">
                  Approved by {formData.reviewer_name || formData.reviewed_by} on {formData.reviewer_signed_at ? new Date(formData.reviewer_signed_at).toLocaleDateString() : (formData.reviewed_at ? new Date(formData.reviewed_at).toLocaleDateString() : 'N/A')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Audit History — admin-only panel. Lets owners answer "did Paul
            click Submit?" without running shell commands. Renders a
            simple time-sorted list of state transitions stored on the
            record by the backend `_append_history` helper. */}
        {canApproveFieldReports() && currentId && Array.isArray(formData.history) && formData.history.length > 0 && (
          <Card className="border-slate-200 mt-6">
            <CardHeader>
              <CardTitle className="text-base font-['Barlow_Condensed'] flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-500" />
                Audit History
                <span className="text-xs font-normal text-slate-400">({formData.history.length} events)</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="locate-audit-history">
                {[...formData.history].reverse().map((h, i) => (
                  <div key={i} className="flex items-start gap-3 text-xs py-1.5 border-b border-slate-100 last:border-b-0">
                    <span className="font-mono text-slate-500 flex-shrink-0 w-32" title={h.ts}>
                      {h.ts ? new Date(h.ts).toLocaleString() : '—'}
                    </span>
                    <span className="font-semibold text-slate-700 w-20 flex-shrink-0 uppercase tracking-wide">
                      {h.action}
                    </span>
                    <span className="text-slate-600 flex-1 min-w-0">
                      <span className="font-medium">{h.user_name || h.user_id || 'unknown'}</span>
                      {h.status_before && h.status_after && h.status_before !== h.status_after && (
                        <span className="ml-2 text-slate-400">
                          {h.status_before} → {h.status_after}
                        </span>
                      )}
                      {h.note && (
                        <span className="ml-2 text-amber-600 italic">{h.note}</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Fixed Bottom Actions - Only show for draft forms */}
      {!isViewOnly && formData.status === 'draft' && (
        <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] md:bottom-0 left-0 right-0 bg-white border-t max-w-2xl mx-auto z-40 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          <div className="px-4 pt-2 pb-1 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate"><strong>Tip:</strong> Save Draft often — Submit will also auto-save your latest changes.</span>
            </div>
            <LastSavedIndicator lastSaved={lastSaved} saving={saving} />
          </div>
          <div className="p-4 flex gap-3">
          {/* Save Draft — neutral outlined, safe action. The Locate workflow
              has historically had techs accidentally tap Submit thinking
              it was Save Draft (the buttons used to look similar). The
              Submit button below is now solid amber w/ a confirm dialog
              so the two paths are visually distinct on mobile. */}
          <Button
            variant="outline"
            size="lg"
            className="flex-1 border-slate-300"
            onClick={() => handleSaveDraft(false)}
            disabled={saving || uploading}
            data-testid="save-draft-btn"
            title={uploading ? 'Wait for photo uploads to finish' : ''}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {uploading ? 'Uploading photos…' : 'Save Draft'}
          </Button>
          <Button
            size="lg"
            className="flex-1 bg-amber-600 hover:bg-amber-700 text-white font-semibold shadow-md"
            onClick={handleSubmit}
            disabled={submitting || uploading}
            data-testid="submit-report-btn"
            title={uploading ? 'Wait for photo uploads to finish' : 'Locks the report and sends to reviewer'}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
            {uploading ? 'Uploading photos…' : 'Submit for Review'}
          </Button>
          </div>
        </div>
      )}
      
      {/* Submitter Edit bar — visible when the original submitter is editing
          their own pending report. Approvers see the Approve/Reject bar below
          instead. */}
      {!isViewOnly && isPending && isSubmitter && !canApproveFieldReports() && (
        <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] md:bottom-0 left-0 right-0 bg-white border-t max-w-2xl mx-auto z-40 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          <div className="px-4 pt-2 pb-1 bg-amber-50 border-b border-amber-200 text-xs text-amber-800 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate"><strong>Pending Review</strong> — you can edit until the reviewer approves. Your signature is locked.</span>
            </div>
            <LastSavedIndicator lastSaved={lastSaved} saving={saving} />
          </div>
          <div className="p-4">
            {photosNotReady && (
              <div className="mb-3 p-2 rounded border border-amber-300 bg-amber-50 text-amber-800 text-xs flex items-start gap-2" data-testid="photo-not-ready-banner-submitter">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{photoBannerText || `${pendingUploads} photo upload${pendingUploads === 1 ? '' : 's'} still in flight — wait for them to finish before saving.`}</span>
              </div>
            )}
            <Button
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleSaveSubmitterEdits}
              disabled={saving || uploading || pendingUploads > 0}
              data-testid="submitter-save-changes-btn"
              title={pendingUploads > 0 ? 'Wait for photo uploads to finish' : ''}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {(uploading || pendingUploads > 0) ? 'Uploading photos…' : 'Save Changes'}
            </Button>
          </div>
        </div>
      )}

      {/* Admin Actions for submitted forms */}
      {canAdminEdit && formData.status === 'submitted_pending_review' && (
        <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] md:bottom-0 left-0 right-0 bg-white border-t p-4 max-w-2xl mx-auto z-40 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          {photosNotReady && (
            <div
              className="mb-3 p-2 rounded border border-amber-300 bg-amber-50 text-amber-800 text-xs flex items-start gap-2"
              data-testid="photo-not-ready-banner"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div>{photoBannerText || `${pendingUploads} photo upload${pendingUploads === 1 ? '' : 's'} still in flight — please wait before approving.`}</div>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await axios.post(`${API}/api/locates/${id}/recompute-photo-counts`, {}, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      toast.success('Photo counts reconciled');
                      // Reload from server to refresh photosNotReady gate.
                      const refreshed = await axios.get(`${API}/api/locates/${id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      setFormData(refreshed.data);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to recompute counts');
                    }
                  }}
                  className="mt-1 text-amber-900 underline font-semibold hover:text-amber-700"
                  data-testid="recompute-photo-counts-btn"
                >
                  Photos look right? → Recompute counts &amp; unlock approval
                </button>
              </div>
            </div>
          )}
          <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
            onClick={async () => {
              const reason = window.prompt('Please enter a rejection reason — the submitter will receive this in their notification and email:');
              if (reason === null) return; // User cancelled
              if (!reason.trim()) {
                toast.error('A rejection reason is required so the submitter knows what to fix.');
                return;
              }
              try {
                await axios.post(`${API}/api/locates/${id}/reject?rejection_reason=${encodeURIComponent(reason)}`, {}, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Report rejected — submitter notified by email and in-app.');
                navigate('/field-reports');
              } catch (error) {
                toast.error('Failed to reject report');
              }
            }}
            data-testid="reject-report-btn"
          >
            <X className="w-4 h-4 mr-2" />
            Reject
          </Button>
          <Button
            className="flex-1 bg-green-600 hover:bg-green-700"
            onClick={handleApprove}
            disabled={approving || photosNotReady}
            data-testid="approve-report-btn"
            title={photosNotReady ? 'Photos still uploading — wait until all are received.' : ''}
          >
            {approving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            Approve Report
          </Button>
          </div>
        </div>
      )}
      
      {/* Print / Save PDF Button for non-admin viewing submitted/completed OR for completed status */}
      {((isViewOnly && !canAdminEdit) || formData.status === 'completed') && (formData.status === 'submitted_pending_review' || formData.status === 'completed') && (
        <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] md:bottom-0 left-0 right-0 bg-white border-t p-4 flex gap-3 max-w-2xl mx-auto z-40 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          <Button
            className="flex-1 bg-orange-600 hover:bg-orange-700"
            onClick={() => navigate(`/locate/${id}/preview`)}
            data-testid="print-preview-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Print / Save PDF
          </Button>
        </div>
      )}
    </div>
  );
}
